import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Users, Search, Loader2, Package, IndianRupee, Mail, Phone, ChevronRight, Building2, Factory } from "lucide-react";

export default function AdminCustomers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [b2bOnly, setB2bOnly] = useState(false);

  const fetchCustomers = async (q) => {
    setLoading(true);
    try {
      let params = q ? `?search=${encodeURIComponent(q)}` : "?";
      if (b2bOnly) params += `&b2b_only=true`;
      const { data } = await api.get(`/admin/customers${params}`);
      setCustomers(data);
    } catch { /* empty */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchCustomers(search); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [b2bOnly]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers(search);
  };

  return (
    <div className="space-y-6" data-testid="admin-customers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fadeInUp">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">Customers</h1>
          <p className="text-sm text-slate-500 mt-1">View and manage all registered customers</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={b2bOnly ? "default" : "outline"}
            onClick={() => setB2bOnly(!b2bOnly)}
            className={`rounded-xl text-xs gap-1.5 ${b2bOnly ? "bg-[#FF6B00] hover:bg-[#E65A00] text-white" : "border-slate-200"}`}
            data-testid="filter-b2b-only"
          >
            <Building2 className="h-3.5 w-3.5" /> {b2bOnly ? "B2B Only" : "All Customers"}
          </Button>
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search name, email, company..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-xl border-slate-200 w-60" data-testid="customer-search" />
            </div>
            <Button type="submit" className="bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl" data-testid="customer-search-btn">Search</Button>
          </form>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden animate-fadeInUp">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin text-[#FF6B00] mx-auto" /></div>
        ) : customers.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No customers found</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-100 bg-slate-50/40">
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 pl-6">Customer</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Contact</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Company</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Orders</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Spent</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Joined</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((c) => (
                <TableRow
                  key={c._id}
                  className="border-slate-50 hover:bg-slate-50/50 cursor-pointer"
                  onClick={() => navigate(`/dashboard/customers/${c._id}`)}
                  data-testid={`customer-row-${c._id}`}
                >
                  <TableCell className="pl-6">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 ${c.is_b2b ? "bg-orange-100" : "bg-blue-100"} rounded-lg flex items-center justify-center`}>
                        <span className={`${c.is_b2b ? "text-[#FF6B00]" : "text-blue-600"} text-sm font-semibold font-['Outfit']`}>{(c.name || "U")[0].toUpperCase()}</span>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-slate-900 block">{c.name}</span>
                        {c.is_b2b && (
                          <span className="inline-flex items-center gap-1 bg-orange-50 text-[#FF6B00] border border-orange-200 px-1.5 py-0.5 rounded text-[9px] font-bold">
                            <Building2 className="h-2.5 w-2.5" /> B2B
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1 text-xs text-slate-600"><Mail className="h-3 w-3" />{c.email}</div>
                      {c.phone && <div className="flex items-center gap-1 text-xs text-slate-500"><Phone className="h-3 w-3" />{c.phone}</div>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {c.is_b2b ? (
                      <div className="space-y-0.5">
                        <p className="text-xs font-semibold text-slate-800">{c.company_name || "—"}</p>
                        {c.gst_number && <p className="text-[10px] text-slate-400 font-mono">{c.gst_number}</p>}
                        {c.industry_type && <p className="text-[10px] text-slate-500"><Factory className="h-2.5 w-2.5 inline" /> {c.industry_type}</p>}
                        {c.monthly_volume && <p className="text-[10px] text-orange-600 font-semibold">{c.monthly_volume}</p>}
                      </div>
                    ) : (
                      <span className="text-[10px] text-slate-300">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-0.5">
                      <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-md text-xs font-semibold">
                        <Package className="h-3 w-3" />{c.order_count}
                      </span>
                      {c.b2b_order_count > 0 && (
                        <span className="block text-[9px] text-[#FF6B00] font-semibold">{c.b2b_order_count} B2B</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm font-semibold text-slate-900 flex items-center gap-0.5">
                      <IndianRupee className="h-3.5 w-3.5" />{c.total_spent?.toLocaleString()}
                    </span>
                  </TableCell>
                  <TableCell><span className="text-xs text-slate-500">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "N/A"}</span></TableCell>
                  <TableCell><ChevronRight className="h-4 w-4 text-slate-400" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
